/**
 * TicketDetailModal Component
 *
 * Modal showing ticket details and chat-style message thread.
 * Allows users to view ticket info and send new messages.
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  XMarkIcon,
  PaperAirplaneIcon,
  UserCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Textarea } from '../../../components/ui/Textarea';
import { Alert } from '../../../components/ui/Alert';
import { TicketStatusBadge } from './TicketStatusBadge';
import { TicketPriorityBadge } from './TicketPriorityBadge';
import { useSupportTicket, useTicketMessages } from '../hooks/useSupportTickets';
import { useCreateTicketMessage } from '../hooks/useCreateTicketMessage';
import type {
  SupportTicketSanitized,
  TicketMessageSanitized,
} from '../../../types/support-ticket.types';
import { categoryLabels } from '../../../types/support-ticket.types';
import { formatRelativeTime } from '../../../lib/utils/format';
import { getErrorMessage } from '../../../lib/utils/errors';

interface TicketDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  ticketId: string;
}

/**
 * Message bubble component
 */
const MessageBubble: React.FC<{
  message: TicketMessageSanitized;
  isCurrentUser: boolean;
}> = ({ message, isCurrentUser }) => {
  const isPlatformAdmin = message.authorType === 'PLATFORM_ADMIN';

  return (
    <div
      className={`flex gap-3 ${isCurrentUser ? 'flex-row-reverse' : 'flex-row'}`}
    >
      {/* Avatar */}
      <div className="flex-shrink-0">
        <div
          className={`h-8 w-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${
            isPlatformAdmin ? 'bg-purple-600' : 'bg-blue-600'
          }`}
        >
          {message.authorName.charAt(0).toUpperCase()}
        </div>
      </div>

      {/* Message content */}
      <div
        className={`flex flex-col max-w-[70%] ${
          isCurrentUser ? 'items-end' : 'items-start'
        }`}
      >
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium text-gray-900">
            {message.authorName}
          </span>
          {isPlatformAdmin && (
            <span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">
              Support
            </span>
          )}
          <span className="text-xs text-gray-500">
            {formatRelativeTime(message.createdAt)}
          </span>
        </div>
        <div
          className={`px-4 py-2 rounded-2xl ${
            isCurrentUser
              ? 'bg-blue-600 text-white rounded-tr-sm'
              : isPlatformAdmin
                ? 'bg-purple-100 text-gray-900 rounded-tl-sm'
                : 'bg-gray-100 text-gray-900 rounded-tl-sm'
          }`}
        >
          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
        </div>

        {/* Attachments */}
        {message.attachments && message.attachments.length > 0 && (
          <div className="mt-2 space-y-1">
            {message.attachments.map((attachment) => (
              <a
                key={attachment.attachmentId}
                href="#"
                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
              >
                📎 {attachment.fileName}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * TicketDetailModal component
 */
export const TicketDetailModal: React.FC<TicketDetailModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  ticketId,
}) => {
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Fetch ticket details
  const {
    data: ticket,
    isLoading: ticketLoading,
    error: ticketError,
  } = useSupportTicket(tenantId, ticketId);

  // Fetch messages
  const {
    data: messagesData,
    isLoading: messagesLoading,
    error: messagesError,
  } = useTicketMessages(tenantId, ticketId);

  // Create message mutation
  const createMessageMutation = useCreateTicketMessage({
    tenantId,
    ticketId,
    onSuccess: () => {
      setNewMessage('');
    },
  });

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messagesData?.items]);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    createMessageMutation.mutate({
      content: newMessage.trim(),
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const isLoading = ticketLoading || messagesLoading;
  const error = ticketError || messagesError;
  const isSending = createMessageMutation.isPending;
  const messages = messagesData?.items || [];

  // Check if ticket is closed (no new messages allowed)
  const isClosed = ticket?.status === 'closed';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" showCloseButton={false}>
      {isLoading ? (
        <div className="p-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      ) : error ? (
        <div className="p-8">
          <Alert variant="error">{getErrorMessage(error)}</Alert>
        </div>
      ) : ticket ? (
        <>
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono text-gray-500">
                    {ticket.referenceNumber}
                  </span>
                  <TicketStatusBadge status={ticket.status} />
                  <TicketPriorityBadge priority={ticket.priority} />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 truncate">
                  {ticket.subject}
                </h2>
                <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
                  <span>{categoryLabels[ticket.category]}</span>
                  <span>&middot;</span>
                  <span>Created {formatRelativeTime(ticket.createdAt)}</span>
                  {ticket.slaBreach && (
                    <>
                      <span>&middot;</span>
                      <span className="flex items-center gap-1 text-red-600">
                        <ExclamationTriangleIcon className="h-4 w-4" />
                        SLA Breached
                      </span>
                    </>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6 max-h-[400px] bg-gray-50">
            {messages.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No messages yet.</p>
              </div>
            ) : (
              messages.map((message) => (
                <MessageBubble
                  key={message.messageId}
                  message={message}
                  isCurrentUser={message.authorType === 'TENANT_ADMIN'}
                />
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Message input */}
          <div className="px-6 py-4 border-t border-gray-200 bg-white">
            {isClosed ? (
              <div className="text-center py-3 text-gray-500 text-sm">
                This ticket has been closed. You cannot send new messages.
              </div>
            ) : (
              <div className="flex gap-3">
                <div className="flex-1">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type your message... (Enter to send, Shift+Enter for new line)"
                    rows={2}
                    className="block w-full px-3 py-2 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm resize-none"
                    disabled={isSending}
                  />
                </div>
                <Button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || isSending}
                  isLoading={isSending}
                  className="self-end"
                >
                  <PaperAirplaneIcon className="h-5 w-5" />
                </Button>
              </div>
            )}

            {createMessageMutation.error && (
              <Alert variant="error" className="mt-3">
                {getErrorMessage(createMessageMutation.error)}
              </Alert>
            )}
          </div>
        </>
      ) : null}
    </Modal>
  );
};
